import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const HERO_STATS = [
  { n: "01", label: "16 focused questions, no software connection required" },
  { n: "02", label: "7 business domains scanned — revenue to management visibility" },
  { n: "03", label: "0 numeric scores shown — categorical, evidence-based findings only" },
];

const PROBLEM_STEPS = [
  {
    n: "01",
    title: "Spot the problem",
    body: "See where revenue, cash, margin or workflow friction may be hurting performance.",
    examples: ["Missed opportunities", "Late payments", "Margin pressure", "Manual work", "Operational delays", "Approval bottlenecks"],
  },
  {
    n: "02",
    title: "Understand what is happening",
    body: "A8A identifies the likely issue and shows the evidence behind the finding.",
  },
  {
    n: "03",
    title: "See what can improve",
    body: "A8A recommends the most appropriate next step based on the problem, evidence and business impact.",
    supporting: "This may include process improvement, digital improvement, analytics, automation or AI where appropriate.",
  },
];

const HOW_STEPS = [
  { n: "01", title: "Tell us what is happening", body: "Answer 16 quick business questions — no software connection required." },
  { n: "02", title: "Create a free account", body: "Takes about 60 seconds. This is what unlocks your results — nothing is shown before this step." },
  { n: "03", title: "See your benchmarked report", body: "Your findings, compared against real, cited industry data — not a generic score." },
  { n: "04", title: "See what should happen next", body: "A8A recommends the strongest next opportunity based on the evidence and your business context." },
];

const BENCHMARK_HIGHLIGHTS = [
  { label: "Gross margin", body: "Compared against real public-market data for your specific industry and sub-type." },
  { label: "Automation ROI", body: "Real, cited savings other organizations report after automating similar work." },
  { label: "Revenue trend", body: "Your own year-over-year change, tracked as you update your numbers." },
];

const EXAMPLE_OUTCOMES = ["Faster collections", "Better cash visibility", "Less manual chasing"];

export default function LandingPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <SEO
        title="A8A — Find Where Your Business Is Losing Money, Time or Control"
        description="A8A helps businesses identify revenue, cash, margin and workflow problems and shows where process improvement, automation or AI can make a measurable difference."
      />
      <Navbar />

      <section className="max-w-6xl mx-auto px-6 pt-20 sm:pt-28 pb-20">
        <div className="grid md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-7">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="landing-positioning-line">
              Business problems first &middot; AI where it makes sense
            </span>
            <h1
              className="font-heading font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.05] mt-5"
              data-testid="landing-hero-headline"
            >
              Find where your business is losing money, time or control.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-lg leading-relaxed" data-testid="landing-hero-subcopy">
              A8A helps you identify operational problems, understand what may be causing them, and see what can improve.
            </p>
            <div className="mt-9 flex flex-col items-start gap-3">
              <Button asChild size="lg" className="px-8 h-12 text-base" data-testid="landing-cta-button">
                <Link to="/scan">Start Free Scan &rarr;</Link>
              </Button>
              <p className="text-xs text-muted-foreground" data-testid="landing-hero-microcopy">Free &middot; No software connection required</p>
            </div>
          </div>
          <div className="hidden md:block md:col-span-5 md:pl-10 md:border-l md:border-border">
            <div className="space-y-7 pt-1">
              {HERO_STATS.map((s, i) => (
                <Reveal key={s.n} delay={i * 120} className="flex gap-4">
                  <span className="font-mono text-sm text-muted-foreground pt-0.5">{s.n}</span>
                  <span className="text-sm text-foreground leading-relaxed">{s.label}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="problem-framework" className="max-w-6xl mx-auto px-6 py-20 border-t border-border">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">The Problem Framework</span>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-3">
          Problem &rarr; Solution &rarr; Outcome
        </h2>
        <div className="grid md:grid-cols-3 gap-10 mt-12">
          {PROBLEM_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 120} data-testid={`problem-step-${s.n}`}>
              <span className="font-mono text-sm text-muted-foreground">{s.n}</span>
              <h3 className="font-heading text-lg font-medium text-foreground mt-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.body}</p>
              {s.supporting && (
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{s.supporting}</p>
              )}
              {s.examples && (
                <ul className="mt-4 space-y-1.5 border-t border-border pt-4">
                  {s.examples.map((ex) => (
                    <li key={ex} className="text-xs text-muted-foreground flex items-center gap-2">
                      <span className="h-1 w-1 bg-muted-foreground" />{ex}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <section id="free-scan" className="max-w-3xl mx-auto px-6 py-20 border-t border-border">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="free-scan-microlabel">
          Free Business Diagnostic
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-foreground mt-3">
          Start with the problem, not the technology.
        </h2>
        <p className="text-base text-muted-foreground mt-4 max-w-lg leading-relaxed">
          Start with a short business scan. A8A identifies where the strongest opportunity may exist.
        </p>
        <p className="text-sm text-muted-foreground mt-2 max-w-lg leading-relaxed">
          Create a free account to see your results, then add a few business details — industry, revenue,
          ideal customers — to unlock a full report benchmarked against real, cited industry data.
        </p>
        <div className="mt-9 border border-border p-7" data-testid="free-scan-card">
          <h3 className="font-heading text-lg font-medium text-foreground">Business Leakage Scan</h3>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Identify where revenue, cash, margin, employee time or operational control may be under pressure.
          </p>
        </div>
        <div className="mt-7 flex flex-col items-start gap-2">
          <Button asChild size="lg" className="px-8 h-12 text-base" data-testid="free-scan-cta-button">
            <Link to="/scan">Start Free &rarr;</Link>
          </Button>
          <p className="text-xs text-muted-foreground">No integrations required.</p>
        </div>
      </section>

      <section id="how-it-works" className="max-w-6xl mx-auto px-6 py-20 border-t border-border">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">The Experience</span>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-foreground mt-3">
          How the experience works
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-12">
          {HOW_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 100} className="border-t border-border pt-5" data-testid={`how-it-works-step-${s.n}`}>
              <span className="font-mono text-sm text-muted-foreground">{s.n}</span>
              <h3 className="font-heading text-base font-medium text-foreground mt-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="benchmarked-report" className="max-w-6xl mx-auto px-6 py-20 border-t border-border">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">The Report</span>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-3 max-w-2xl">
          Benchmarked against real data, not a generic score.
        </h2>
        <p className="text-base text-muted-foreground mt-4 max-w-lg leading-relaxed">
          Every number in your report is cited — sourced from public industry margin data and
          published automation research, not invented.
        </p>
        <div className="grid sm:grid-cols-3 gap-10 mt-12">
          {BENCHMARK_HIGHLIGHTS.map((b, i) => (
            <Reveal key={b.label} delay={i * 120} className="border-t border-border pt-5" data-testid={`benchmark-highlight-${i}`}>
              <h3 className="font-heading text-base font-medium text-foreground">{b.label}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{b.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-20 border-t border-border">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Collections &amp; Cash</span>
        <div className="mt-4 border border-border p-7 space-y-5" data-testid="example-result-card">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What A8A found</p>
            <p className="text-sm text-foreground mt-1.5 leading-relaxed">Customer payments frequently require repeated manual follow-up.</p>
          </div>
          <div className="border-t border-border pt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Why it matters</p>
            <p className="text-sm text-foreground mt-1.5 leading-relaxed">Late collections reduce cash visibility and create unnecessary manual work.</p>
          </div>
          <div className="border-t border-border pt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What could improve</p>
            <p className="text-sm text-foreground mt-1.5 leading-relaxed">Structured collections prioritisation, promise tracking and escalation.</p>
          </div>
          <div className="border-t border-border pt-4" data-testid="example-benchmark-citation">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Benchmarked against</p>
            <p className="text-sm text-foreground mt-1.5 leading-relaxed">
              Finance teams that modernize collections report ~30% productivity gains in accounts receivable.
            </p>
            <p className="text-xs text-muted-foreground mt-1.5">Source: Deloitte, "Future of Finance" (2025).</p>
          </div>
          <div className="border-t border-border pt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What this could improve</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {EXAMPLE_OUTCOMES.map((c) => (
                <span key={c} className="font-mono text-xs border border-border px-3 py-1 text-muted-foreground">{c}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-7 text-left">
          <Button asChild size="lg" className="px-8 h-12 text-base" data-testid="example-result-cta-button">
            <Link to="/scan">Find My Opportunity &rarr;</Link>
          </Button>
        </div>
      </section>

      <section className="bg-foreground text-background py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight" data-testid="final-cta-headline">
            Find what is worth fixing first.
          </h2>
          <p className="text-base text-background/70 mt-4 max-w-lg leading-relaxed">
            Start with a short business scan. Understand the problem before deciding what should happen next.
          </p>
          <div className="mt-8 flex flex-col items-start gap-2">
            <Button asChild size="lg" variant="secondary" className="px-8 h-12 text-base" data-testid="final-cta-button">
              <Link to="/scan">Start Free &rarr;</Link>
            </Button>
            <p className="text-xs text-background/60">No software connection required.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="font-heading text-base font-medium text-foreground">A8A</p>
            <p className="text-xs text-muted-foreground mt-1">Business problems first. AI where it makes sense.</p>
          </div>
          <div className="flex items-center gap-5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <a href="mailto:growth@a8a.solutions" className="hover:text-foreground transition-colors" data-testid="footer-contact-email-link">Contact</a>
            <Link to="/login" className="hover:text-foreground transition-colors">Sign In</Link>
          </div>
        </div>
        <p className="text-left sm:text-center text-xs text-muted-foreground mt-6 max-w-6xl mx-auto px-6">
          Provisional business diagnostics. Not financial, legal or professional advice. Outcomes are not guaranteed.
        </p>
        <p className="text-left sm:text-center text-xs text-muted-foreground mt-2 max-w-6xl mx-auto px-6">&copy; {new Date().getFullYear()} A8A.solutions</p>
      </footer>
    </div>
  );
}
